import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, KeyRound, Lock } from 'lucide-react';

const ResetPassword: React.FC = () => {
  const { t } = useLanguage();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emailFromUrl = useMemo(() => searchParams.get('email')?.trim() ?? '', [searchParams]);
  const tokenFromUrl = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const linkInvalid = !emailFromUrl || !tokenFromUrl;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (linkInvalid) {
      setError(t('invalidResetLink'));
      return;
    }
    if (!password) {
      setError(t('passwordRequired'));
      return;
    }
    if (password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(emailFromUrl, tokenFromUrl, password);
      navigate('/login', { replace: true, state: { passwordResetSuccess: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('passwordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-3xl">{t('resetPasswordTitle')}</CardTitle>
          <CardDescription className="text-center">{t('resetPasswordDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {linkInvalid ? (
            <div className="space-y-4">
              <Alert variant="destructive">{t('invalidResetLink')}</Alert>
              <Button variant="outline" className="w-full" asChild>
                <RouterLink to="/forgot-password">{t('recoverPasswordTitle')}</RouterLink>
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <RouterLink to="/login" className="text-primary hover:underline">
                  {t('backToSignIn')}
                </RouterLink>
              </p>
            </div>
          ) : (
            <>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  {error}
                </Alert>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <Label>{t('emailAddress')}</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input readOnly value={emailFromUrl} className="cursor-default bg-muted/50 pl-9" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reset-password">{t('newPassword')}</Label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reset-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reset-confirm">{t('confirmPassword')}</Label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reset-confirm"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9 pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  <Lock className="h-4 w-4" />
                  {isLoading ? t('updatingPassword') : t('setNewPassword')}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  <RouterLink to="/login" className="text-primary hover:underline">
                    {t('backToSignIn')}
                  </RouterLink>
                </p>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
